import { ButtonGroup, Icon, SvgIconNames } from '@toggle/design-system';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useSubscription } from '~/stores/use-subscription/useSubscription';
import {
  ChatMode,
  useChatNavigation,
} from '~/views/alfred/use-chat-navigation/useChatNavigation';

import { TooltipContainer } from '../tooltip-container/TooltipContainer';
import styles from './ChatModesButtonGroup.module.scss';

type ChatModesButtonGroupType = {
  chatMode: ChatMode;
  icon: SvgIconNames;
};

export interface ChatModesButtonGroupProps {
  compactMode?: boolean;
  variant?: 'primary' | 'secondary';
}

export const ChatModesButtonGroup = ({
  compactMode,
  variant = 'primary',
}: ChatModesButtonGroupProps) => {
  const { t } = useTranslation(['chat']);
  const { mode, setMode } = useChatNavigation();

  const isBasicUser = useSubscription(state => state.isBasicUser());

  const options: ChatModesButtonGroupType[] = [
    (isBasicUser
      ? { chatMode: 'quickAnalysis', icon: 'QuickAnalysis' }
      : {
          chatMode: 'research',
          icon: 'Alfred',
        }) as ChatModesButtonGroupType,
    { chatMode: 'document', icon: 'DocSearch' },
  ];

  const activeIndex = options.findIndex(option => mode === option.chatMode);

  return (
    <ButtonGroup
      className={clsx(styles.styledButtonGroup, {
        [styles.secondary]: variant === 'secondary',
      })}
      activeIndex={activeIndex}
      customActiveBackgroundColor={
        variant === 'secondary' ? 'var(--surface-hover)' : undefined
      }
      labels={options.map((o, idx) => {
        /* i18next-extract-mark-context-next-line ["research", "quickAnalysis", "document"] */
        const label =
          !isBasicUser && o.chatMode === 'research'
            ? t('chat:askAlfred')
            : t('chat:analysis', { context: o.chatMode });

        return (
          <TooltipContainer
            key={o.chatMode}
            label={label}
            inPortal
            disabled={!compactMode}
            placement="top-start"
            offsetY={-15}
            offsetX={-5}
          >
            <div
              className={clsx(styles.wrapper, {
                [styles.active]: activeIndex === idx,
              })}
              data-testid={`chat-mode-button-${o.chatMode}`}
              data-is-active={activeIndex === idx}
            >
              {<Icon size={16} iconName={o.icon} />}
              {!compactMode && label}
            </div>
          </TooltipContainer>
        );
      })}
      onClick={index => {
        setMode(options[index].chatMode);
      }}
    />
  );
};
